import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ExamPaperDetail, ExamPaperUpdate, updateExamPaper } from '@/api/exam';
import { Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';
import { useLookupStore } from '@/store/lookupStore';
import { REGIONS } from '@/constants/exam';

// ─── 表单 Schema ───────────────────────────────────────────────

const formSchema = z.object({
  name: z.string().min(1, '试卷名称不能为空'),
  stage: z.string().optional(),
  subject: z.string().optional(),
  grade: z.string().optional(),
  year: z.coerce.number().optional(),
  category: z.string().optional(),
  exam_type: z.string().optional(),
  province: z.string().optional(),
  city: z.string().optional(),
  source_book: z.string().optional(),
  chapter: z.string().optional(),
});

interface MetadataFormProps {
  paper: ExamPaperDetail;
  onUpdate: (updatedPaper: ExamPaperDetail) => void;
}

// ─── 组件 ──────────────────────────────────────────────────────

export const MetadataForm: React.FC<MetadataFormProps> = ({ paper, onUpdate }) => {
  const [isSaving, setIsSaving] = React.useState(false);

  // Lookup store
  const {
    stages, grades, subjects, examTypes, competitions,
    fetchAll, fetchGradesByStage, fetchCompetitionsBySubject,
  } = useLookupStore()

  // 初始化加载 lookup 数据
  React.useEffect(() => {
    fetchAll()
  }, [fetchAll])

  const getFormValues = React.useCallback(() => ({
    name: paper.name || '',
    stage: paper.stage || '',
    subject: paper.subject || '',
    grade: paper.grade || '',
    year: paper.year || undefined,
    category: paper.category || '校内',
    exam_type: paper.exam_type || '',
    province: paper.province || '',
    city: paper.city || '',
    source_book: paper.source_book || '',
    chapter: paper.chapter || '',
  }), [paper])

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: getFormValues(),
  });

  // paper 变化时重置表单
  React.useEffect(() => {
    form.reset(getFormValues())
  }, [paper, form, getFormValues])

  // 监听学段变化，加载年级
  const watchedStage = form.watch('stage')
  React.useEffect(() => {
    if (watchedStage) {
      fetchGradesByStage(watchedStage)
    }
  }, [watchedStage, fetchGradesByStage])

  // 当前学段可用的学科和年级
  const currentSubjects = subjects.filter(s => s.type === 'regular')
  const currentGrades = grades.filter(g => g.stageCode === watchedStage)

  // 省份-城市联动 (使用常量)
  const watchedProvince = form.watch('province') || ''
  const selectedProvince = REGIONS.find(p => p.name === watchedProvince)
  const cityRegions = selectedProvince?.children || []

  // 类别和考试类型处理
  const watchedCategory = form.watch('category') || ''
  const watchedExamType = form.watch('exam_type') || ''
  // 根据类别选择不同的预定义类型列表
  const predefinedTypes = watchedCategory === '竞赛'
    ? competitions.map(c => c.code)
    : examTypes.map(t => t.code)
  const isOtherExamType = watchedExamType === 'other' || (!!watchedExamType && !predefinedTypes.includes(watchedExamType))
  const examTypeSelectValue = isOtherExamType ? 'other' : watchedExamType

  // 竞赛时按学科加载竞赛列表
  const watchedSubject = form.watch('subject')
  React.useEffect(() => {
    if (watchedCategory === '竞赛' && watchedSubject) {
      fetchCompetitionsBySubject(watchedSubject)
    }
  }, [watchedCategory, watchedSubject, fetchCompetitionsBySubject])

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    setIsSaving(true);
    try {
      const payload: ExamPaperUpdate = {
        name: values.name,
        stage: values.stage || null,
        subject: values.subject || null,
        grade: values.grade || null,
        year: values.year || null,
        category: values.category || null,
        exam_type: values.exam_type || null,
        province: values.province || null,
        city: values.city || null,
        source_book: values.source_book || null,
        chapter: values.chapter || null,
      };

      const updatedPaper = await updateExamPaper(paper.id, payload);
      onUpdate(updatedPaper);
      toast.success('试卷信息已更新');
    } catch (error: any) {
      toast.error(error.message || '更新失败');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">基本信息</CardTitle>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>试卷名称</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-3 gap-4">
              <FormField
                control={form.control}
                name="stage"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>学段</FormLabel>
                    <Select
                      value={field.value}
                      onValueChange={(v) => {
                        field.onChange(v);
                        form.setValue('subject', '');
                        form.setValue('grade', '');
                      }}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="选择学段" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {stages.filter(s => s.code !== '大学').map((s) => (
                          <SelectItem key={s.code} value={s.code}>{s.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="subject"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>学科</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange} disabled={!watchedStage}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder={watchedStage ? "选择学科" : "先选择学段"} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {currentSubjects.map((s) => (
                          <SelectItem key={s.code} value={s.code}>{s.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="grade"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>年级</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange} disabled={!watchedStage}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder={watchedStage ? "选择年级" : "先选择学段"} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {currentGrades.map((g) => (
                          <SelectItem key={g.code} value={g.code}>{g.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <FormField
                control={form.control}
                name="year"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>年份</FormLabel>
                    <FormControl>
                      <Input type="number" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="province"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>省份</FormLabel>
                    <Select
                      value={field.value || '__none__'}
                      onValueChange={(v) => {
                        field.onChange(v === '__none__' ? '' : v);
                        form.setValue('city', '');
                      }}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="选择省份" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="__none__">未选择</SelectItem>
                        {REGIONS.map((r) => (
                          <SelectItem key={r.name} value={r.name}>{r.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="city"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>城市</FormLabel>
                    <Select
                      value={field.value || '__none__'}
                      onValueChange={(v) => field.onChange(v === '__none__' ? '' : v)}
                      disabled={!watchedProvince}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder={watchedProvince ? "选择城市" : "先选择省份"} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="__none__">未选择</SelectItem>
                        {cityRegions.map((c) => (
                          <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="category"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>类别</FormLabel>
                  <Select
                    value={field.value || '校内'}
                    onValueChange={(v) => {
                      field.onChange(v)
                      // 切换类别时清空考试类型
                      form.setValue('exam_type', '')
                    }}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="校内">校内</SelectItem>
                      <SelectItem value="竞赛">竞赛</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {watchedCategory === '校内' && (
              <FormField
                control={form.control}
                name="exam_type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>考试类型</FormLabel>
                    <Select
                      value={examTypeSelectValue || undefined}
                      onValueChange={(v) => {
                        if (v === 'other') {
                          form.setValue('exam_type', 'other', { shouldDirty: true })
                          return
                        }
                        field.onChange(v)
                      }}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="选择考试类型" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {examTypes.filter(t => t.code !== '其他').map((t) => (
                          <SelectItem key={t.code} value={t.code}>{t.name}</SelectItem>
                        ))}
                        <SelectItem value="other">其他</SelectItem>
                      </SelectContent>
                    </Select>
                    {isOtherExamType && (
                      <div className="pt-2">
                        <FormControl>
                          <Input
                            placeholder="请输入考试类型"
                            value={watchedExamType === 'other' ? '' : watchedExamType}
                            onChange={(e) => form.setValue('exam_type', e.target.value, { shouldDirty: true })}
                          />
                        </FormControl>
                      </div>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {watchedCategory === '竞赛' && (
              <FormField
                control={form.control}
                name="exam_type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>竞赛名称</FormLabel>
                    <Select
                      value={examTypeSelectValue || undefined}
                      onValueChange={(v) => {
                        if (v === 'other') {
                          form.setValue('exam_type', 'other', { shouldDirty: true })
                          return
                        }
                        field.onChange(v)
                      }}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="选择竞赛" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {competitions.map((c) => (
                          <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>
                        ))}
                        <SelectItem value="other">其他</SelectItem>
                      </SelectContent>
                    </Select>
                    {isOtherExamType && (
                      <div className="pt-2">
                        <FormControl>
                          <Input
                            placeholder="请输入竞赛名称"
                            value={watchedExamType === 'other' ? '' : watchedExamType}
                            onChange={(e) => form.setValue('exam_type', e.target.value, { shouldDirty: true })}
                          />
                        </FormControl>
                      </div>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="source_book"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>来源书本</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="如：人教版七年级上册" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="chapter"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>章节</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="如：第三章 一次函数" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <Button type="submit" className="w-full" disabled={isSaving}>
              {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
              保存基本信息
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};
